'use client';

import React, { useState } from 'react';
import { Smartphone, Bell, CheckCircle, Zap, Shield, BarChart2 } from 'lucide-react';
import { useSite } from '@/context/SiteContext';

const FEATURES = [
  { icon: BarChart2, label: 'Real-Time Quotes',    desc: 'Live prices and charts right in your pocket.' },
  { icon: Bell,      label: 'Price Alerts',         desc: 'Get notified the moment a stock hits your target.' },
  { icon: Zap,       label: 'Instant Screener',     desc: 'Run powerful screens on the go in seconds.' },
  { icon: Shield,    label: 'Secure & Private',     desc: 'Your data stays yours. Always.' },
];

export default function MobilePage() {
  const { siteName } = useSite();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'done'>('idle');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setStatus('done');
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-sky-500/5 via-transparent to-transparent pointer-events-none" />
        <div className="max-w-3xl mx-auto px-2 pt-20 pb-16 text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-sky-500/10 border border-sky-500/20 text-sky-400 text-xs font-semibold px-4 py-1.5 rounded-full mb-8">
            <Smartphone size={12} />
            COMING SOON
          </div>

          {/* Phone icon */}
          <div className="flex justify-center mb-8">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-sky-500/20 to-sky-600/10 border border-sky-500/20 flex items-center justify-center">
              <Smartphone size={40} className="text-sky-400" />
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl font-extrabold text-white mb-4 leading-tight tracking-tight">
            {siteName} Mobile
          </h1>

          <p className="text-xl text-sky-400 font-semibold mb-4">
            Your market intelligence, in your pocket.
          </p>

          <p className="text-fg-muted text-base leading-relaxed mb-10 max-w-xl mx-auto">
            We&apos;re building the {siteName} mobile app for iOS and Android. Sign up to be
            notified when it launches.
          </p>

          {/* Notify Me form */}
          <div className="max-w-xl mx-auto">
            {status === 'done' ? (
              <div className="flex items-center justify-center gap-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl px-5 py-4 text-sm">
                <CheckCircle size={16} className="flex-shrink-0" />
                Thanks! We&apos;ll be in touch.
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  className="flex-1 bg-card-bg border border-default focus:border-sky-500 rounded-xl px-4 py-3 text-sm text-slate-200 placeholder-slate-500 outline-none transition-colors"
                />
                <button
                  type="submit"
                  className="px-6 py-3 bg-sky-500 hover:bg-sky-400 text-white font-semibold text-sm rounded-xl transition-colors whitespace-nowrap"
                >
                  Notify Me
                </button>
              </form>
            )}
            <p className="mt-3 text-xs text-slate-600">
              No spam. We&apos;ll only reach out when the app is ready.
            </p>
          </div>
        </div>
      </section>

      {/* App badge placeholders */}
      <section className="max-w-3xl mx-auto px-2 pb-12">
        <p className="text-center text-xs text-slate-600 uppercase tracking-widest font-semibold mb-5">
          Available Soon On
        </p>
        <div className="flex flex-wrap justify-center gap-4">
          {/* App Store placeholder */}
          <div className="flex items-center gap-3 bg-card-bg border border-default rounded-xl px-5 py-3 opacity-50 cursor-not-allowed select-none">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="text-fg-muted">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
            </svg>
            <div>
              <div className="text-[10px] text-fg-subtle">Download on the</div>
              <div className="text-sm font-semibold text-slate-300">App Store</div>
              <div className="text-[10px] text-sky-500 font-medium">Coming Soon</div>
            </div>
          </div>

          {/* Google Play placeholder */}
          <div className="flex items-center gap-3 bg-card-bg border border-default rounded-xl px-5 py-3 opacity-50 cursor-not-allowed select-none">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" className="text-fg-muted">
              <path d="M3.18 23.76c.3.17.64.24.99.2L15.77 12 12 8.23zM20.54 10.27l-2.83-1.62-3.99 3.99 3.99 3.99 2.86-1.63A1.99 1.99 0 0 0 20.54 10.27zM3.55.31A1.99 1.99 0 0 0 3 1.73v20.54l10.14-10.14z" />
            </svg>
            <div>
              <div className="text-[10px] text-fg-subtle">Get it on</div>
              <div className="text-sm font-semibold text-slate-300">Google Play</div>
              <div className="text-[10px] text-sky-500 font-medium">Coming Soon</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features grid */}
      <section className="max-w-4xl mx-auto px-2 py-12">
        <h2 className="text-center text-xl font-bold text-white mb-8">What to expect</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {FEATURES.map(({ icon: Icon, label, desc }) => (
            <div key={label} className="flex items-start gap-4 bg-card-bg border border-default rounded-xl p-5">
              <div className="w-9 h-9 rounded-lg bg-sky-500/10 flex items-center justify-center flex-shrink-0">
                <Icon size={17} className="text-sky-400" />
              </div>
              <div>
                <div className="text-sm font-semibold text-white mb-0.5">{label}</div>
                <div className="text-xs text-fg-muted leading-relaxed">{desc}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="max-w-3xl mx-auto px-2 py-12 text-center">
        <div className="bg-card-bg border border-default rounded-2xl p-8">
          <Smartphone size={32} className="text-sky-400 mx-auto mb-4" />
          <h2 className="text-lg font-bold text-white mb-3">Be the first to know</h2>
          <p className="text-fg-muted text-sm leading-relaxed max-w-md mx-auto">
            We&apos;re working hard to bring the full {siteName} experience to mobile. Leave your
            email above and we&apos;ll notify you the moment it&apos;s live on the App Store and
            Google Play.
          </p>
        </div>
      </section>
    </div>
  );
}
